import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { METRICS, METRIC_CATEGORIES, type MetricDef } from './metricsRegistry'
import { SearchInput, CrmToaster } from './crmUi'

/**
 * Metrics glossary (PRD-02 §5.1) — every number in the CRM, defined in plain English, with the
 * calculation collapsed behind a toggle, searchable (aliases included), stable #anchors with
 * scroll-and-highlight, incomplete definitions FLAGGED rather than hidden, and printable for
 * the data room.
 */

const Entry: React.FC<{ m: MetricDef; highlighted: boolean }> = ({ m, highlighted }) => {
  const [showCalc, setShowCalc] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { if (highlighted && ref.current) ref.current.scrollIntoView({ block: 'center' }) }, [highlighted])
  return (
    <div ref={ref} id={m.id} className={`rounded-xl border p-4 bg-surface transition-shadow ${highlighted ? 'border-brand-400 shadow-[0_0_0_3px_rgba(37,99,235,0.15)]' : 'border-border'} ${m.parentMetricId ? 'ml-6' : ''}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[15px] font-bold text-txt-primary">
            {m.displayName}
            {m.incomplete && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">definition incomplete</span>}
          </p>
          {m.aliases.length > 0 && <p className="text-[11px] text-txt-tertiary">also called: {m.aliases.join(' · ')}</p>}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          {m.calcPageRoute && <Link to={m.calcPageRoute} className="text-brand-600 hover:underline">Calculation page →</Link>}
          <a href={`#${m.id}`} className="text-txt-tertiary hover:text-txt-primary" title="Anchor link">#</a>
        </div>
      </div>
      <p className="text-[13px] text-txt-secondary mt-2">{m.plainDefinition}</p>
      {m.incomplete && m.incompleteNote && <p className="text-[12px] text-amber-700 mt-1">{m.incompleteNote}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-[12px]">
        {m.inclusions.length > 0 && <p><span className="font-semibold text-txt-primary">Counts:</span> <span className="text-txt-secondary">{m.inclusions.join('; ')}</span></p>}
        {m.exclusions.length > 0 && <p><span className="font-semibold text-txt-primary">Does not count:</span> <span className="text-txt-secondary">{m.exclusions.join('; ')}</span></p>}
        <p><span className="font-semibold text-txt-primary">Sources:</span> <span className="text-txt-tertiary">{m.sourceTables.join(', ')}</span></p>
        <p><span className="font-semibold text-txt-primary">Basis:</span> <span className="text-txt-tertiary">{m.timeBasis} · {m.refreshFrequency}</span></p>
      </div>
      <button onClick={() => setShowCalc((v) => !v)} className="mt-2 text-[11px] font-medium text-brand-600 hover:underline">
        {showCalc ? 'Hide' : 'Show'} calculation
      </button>
      {showCalc && <pre className="mt-1.5 text-[11px] text-txt-secondary bg-surface-secondary rounded-lg p-2.5 whitespace-pre-wrap">{m.technicalFormula}</pre>}
      <p className="text-[10px] text-txt-tertiary mt-2">Last reviewed {m.lastReviewed} · {m.reviewedBy}</p>
    </div>
  )
}

export const CrmMetricsGlossary: React.FC = () => {
  const [q, setQ] = useState('')
  const location = useLocation()
  const anchor = location.hash.replace('#', '')

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return METRICS
    return METRICS.filter((m) => `${m.displayName} ${m.aliases.join(' ')} ${m.plainDefinition}`.toLowerCase().includes(needle))
  }, [q])

  // Parents first, children nested directly beneath their parent.
  const ordered = (list: MetricDef[]) => {
    const out: MetricDef[] = []
    for (const m of list.filter((x) => !x.parentMetricId)) {
      out.push(m)
      out.push(...list.filter((x) => x.parentMetricId === m.id))
    }
    // Orphans whose parent didn't match the search still show.
    for (const m of list) if (!out.includes(m)) out.push(m)
    return out
  }

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      <div className="px-6 pt-6 pb-3 bg-surface border-b border-border shrink-0 flex items-start justify-between gap-3 flex-wrap print:hidden">
        <div>
          <h1 className="text-xl font-bold text-txt-primary">Metrics glossary</h1>
          <p className="text-sm text-txt-secondary">Every number in the CRM — what it means, what it counts, where it comes from</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search metrics, aliases, definitions…" className="w-72" />
          <button onClick={() => window.print()} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover">
            Print / PDF
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-6 space-y-6">
        {METRIC_CATEGORIES.map((cat) => {
          const list = ordered(filtered.filter((m) => m.category === cat.key))
          if (!list.length) return null
          return (
            <div key={cat.key}>
              <h2 className="text-[13px] font-bold uppercase tracking-wider text-txt-tertiary mb-2">{cat.label}</h2>
              <div className="space-y-3">
                {list.map((m) => <Entry key={m.id} m={m} highlighted={anchor === m.id} />)}
              </div>
            </div>
          )
        })}
        {filtered.length === 0 && <p className="text-sm text-txt-tertiary text-center py-10">No metrics match "{q}" — aliases are searched too.</p>}
      </div>
    </div>
  )
}
